/**
 * 工序模块常量定义
 *
 * @description 全局工序定义的常量
 * @module types/operation/constants
 * @category Operation - 工序管理
 *
 * @last-updated 2025-12-12
 */

import { OperationType, OperationInspectionType, OperationStatus, RouteOperationStatus } from './enums';

/**
 * 工序类型标签
 */
export const OPERATION_TYPE_LABELS: Record<OperationType, string> = {
  [OperationType.PROCESSING]: '加工',
  [OperationType.ASSEMBLY]: '装配',
  [OperationType.INSPECTION]: '检验',
  [OperationType.PACKAGING]: '包装',
  [OperationType.MATERIAL_PREP]: '备料',
  [OperationType.TRANSFER]: '转移',
  [OperationType.REWORK]: '返工',
  [OperationType.OTHER]: '其他',
};

/**
 * 工序类型选项
 */
export const OPERATION_TYPE_OPTIONS = Object.entries(OPERATION_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/**
 * 检验类型标签
 */
export const OPERATION_INSPECTION_TYPE_LABELS: Record<OperationInspectionType, string> = {
  [OperationInspectionType.NONE]: '无需检验',
  [OperationInspectionType.SELF]: '自检',
  [OperationInspectionType.PATROL]: '巡检',
  [OperationInspectionType.FULL]: '全检',
  [OperationInspectionType.SAMPLING]: '抽检',
  [OperationInspectionType.FIRST_ARTICLE]: '首件检验',
};

/**
 * 检验类型选项
 */
export const OPERATION_INSPECTION_TYPE_OPTIONS = Object.entries(OPERATION_INSPECTION_TYPE_LABELS).map(
  ([value, label]) => ({
    value,
    label,
  }),
);

/**
 * 工序状态标签
 */
export const OPERATION_STATUS_LABELS: Record<OperationStatus, string> = {
  [OperationStatus.ACTIVE]: '启用',
  [OperationStatus.INACTIVE]: '停用',
  [OperationStatus.DRAFT]: '草稿',
};

/**
 * 工序状态选项
 */
export const OPERATION_STATUS_OPTIONS = Object.entries(OPERATION_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/**
 * 工序状态标签样式
 */
export const OPERATION_STATUS_TAGS: Record<OperationStatus, { color: string; text: string }> = {
  [OperationStatus.ACTIVE]: { color: 'success', text: '启用' },
  [OperationStatus.INACTIVE]: { color: 'error', text: '停用' },
  [OperationStatus.DRAFT]: { color: 'warning', text: '草稿' },
};

/**
 * 工序类型标签样式
 */
export const OPERATION_TYPE_TAGS: Record<OperationType, { color: string; text: string }> = {
  [OperationType.PROCESSING]: { color: 'processing', text: '加工' },
  [OperationType.ASSEMBLY]: { color: 'blue', text: '装配' },
  [OperationType.INSPECTION]: { color: 'orange', text: '检验' },
  [OperationType.PACKAGING]: { color: 'cyan', text: '包装' },
  [OperationType.MATERIAL_PREP]: { color: 'purple', text: '备料' },
  [OperationType.TRANSFER]: { color: 'geekblue', text: '转移' },
  [OperationType.REWORK]: { color: 'volcano', text: '返工' },
  [OperationType.OTHER]: { color: 'default', text: '其他' },
};

/**
 * 检验类型标签样式
 */
export const OPERATION_INSPECTION_TYPE_TAGS: Record<OperationInspectionType, { color: string; text: string }> = {
  [OperationInspectionType.NONE]: { color: 'default', text: '无需检验' },
  [OperationInspectionType.SELF]: { color: 'blue', text: '自检' },
  [OperationInspectionType.PATROL]: { color: 'cyan', text: '巡检' },
  [OperationInspectionType.FULL]: { color: 'orange', text: '全检' },
  [OperationInspectionType.SAMPLING]: { color: 'purple', text: '抽检' },
  [OperationInspectionType.FIRST_ARTICLE]: { color: 'gold', text: '首件检验' },
};

/**
 * 工艺路线工序状态标签
 */
export const ROUTE_OPERATION_STATUS_LABELS: Record<RouteOperationStatus, string> = {
  [RouteOperationStatus.ACTIVE]: '启用',
  [RouteOperationStatus.INACTIVE]: '停用',
};

/**
 * 工艺路线工序状态选项
 */
export const ROUTE_OPERATION_STATUS_OPTIONS = Object.entries(ROUTE_OPERATION_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/**
 * 工艺路线工序状态标签样式
 */
export const ROUTE_OPERATION_STATUS_TAGS: Record<RouteOperationStatus, { color: string; text: string }> = {
  [RouteOperationStatus.ACTIVE]: { color: 'success', text: '启用' },
  [RouteOperationStatus.INACTIVE]: { color: 'error', text: '停用' },
};
